import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOffers,
  updateOffer,
  createOffer,
  deleteOffer,
  fetchOffer,
  fetchOfferRevisions,
  fetchOfferRevision,
  fetchOfferActivity,
  createOfferRevision,
  acceptOffer,
  approveOfferRevision,
  rejectOfferRevision,
  submitOfferRevisionForReview,
  convertOfferToProject,
  generateOfferLinesFromCalculation,
} from "../api/offer";
import { normalizeError, hasMeaningfulErrors } from "../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  offer: {},
  totalCount: 0,
  currentCount: 0,
  newestOffersCount: 0,
  // Company-level Offer rollout flags (list envelope + mirrored on offer JSON).
  offerFeatureFlags: {},
  // Document & Revision (flag offer_revisions)
  revisions: [],
  revisionsOfferId: null,
  revisionsLoading: false,
  revisionsError: null,
  currentDraftRevisionId: null,
  acceptedRevisionId: null,
  // Activity timeline (Historikk tab)
  activity: [],
  activityOfferId: null,
  activityCategory: "all",
  activityLoading: false,
  activityError: null,
};

/** Prefer list-envelope / offer JSON flags; never wipe previous with undefined. */
function resolveOfferFeatureFlags(payload, previous = {}) {
  if (payload?.offer_feature_flags && typeof payload.offer_feature_flags === "object") {
    return payload.offer_feature_flags;
  }
  const fromRow = payload?.data?.[0]?.offer_feature_flags;
  if (fromRow && typeof fromRow === "object") {
    return fromRow;
  }
  return previous || {};
}

export const initialController = {
  filters: [],
  page: 1,
  per_page: 200,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const deleteOfferAsync = createAsyncThunk(
  "offers/deleteOfferAsync",
  async (payload, { rejectWithValue }) => {
    try {
      return await deleteOffer(payload);
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "delete_offer");
    }
  }
);

export const createOfferAsync = createAsyncThunk(
  "offers/createOfferAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createOffer(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_offer");
      }
      return response;
    } catch (error) {
      return rejectNormalized(rejectWithValue, error, "create_offer");
    }
  }
);

export const updateOfferAsync = createAsyncThunk(
  "offers/updateOfferAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateOffer(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_offer");
      }
      return response;
    } catch (error) {
      return rejectNormalized(rejectWithValue, error, "update_offer");
    }
  }
);

export const getOffersAsync = createAsyncThunk(
  "offers/getOffersAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchOffers(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_offers");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_offers");
    }
  }
);

export const getOfferAsync = createAsyncThunk(
  "offers/getOfferAsync",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchOffer(id);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "get_offer");
      }
      return response;
    } catch (error) {
      return rejectNormalized(rejectWithValue, error, "get_offer");
    }
  }
);

function isOfferThunkError(response) {
  if (!response) return true;
  if (response.status === 403) return true;
  if (response.error || response.errors) return true;
  return false;
}

export const getOfferRevisionsAsync = createAsyncThunk(
  "offers/getOfferRevisionsAsync",
  async (offerId, { rejectWithValue }) => {
    try {
      const response = await fetchOfferRevisions(offerId);
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return { offerId, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getOfferActivityAsync = createAsyncThunk(
  "offers/getOfferActivityAsync",
  async ({ offerId, category = "all" }, { rejectWithValue }) => {
    try {
      const response = await fetchOfferActivity(offerId, { category });
      // Missing endpoint during development → empty feed, not an error UI.
      if (response?.missing || response?.status === 404) {
        return { offerId, category, data: [] };
      }
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return { offerId, category, data: response?.data || [] };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getOfferRevisionAsync = createAsyncThunk(
  "offers/getOfferRevisionAsync",
  async ({ offerId, revisionId }, { rejectWithValue }) => {
    try {
      const response = await fetchOfferRevision(offerId, revisionId);
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createOfferRevisionAsync = createAsyncThunk(
  "offers/createOfferRevisionAsync",
  async ({ offerId, ...payload }, { rejectWithValue }) => {
    try {
      const response = await createOfferRevision(offerId, payload);
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return { offerId, revision: response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const acceptOfferAsync = createAsyncThunk(
  "offers/acceptOfferAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await acceptOffer(payload);
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const approveOfferRevisionAsync = createAsyncThunk(
  "offers/approveOfferRevisionAsync",
  async ({ offerId, revisionId, ...payload }, { rejectWithValue }) => {
    try {
      const response = await approveOfferRevision(offerId, revisionId, payload);
      if (response?.missing || response?.status === 404) {
        return rejectWithValue({ ...response, missing: true });
      }
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return { offerId, revisionId, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const rejectOfferRevisionAsync = createAsyncThunk(
  "offers/rejectOfferRevisionAsync",
  async ({ offerId, revisionId, ...payload }, { rejectWithValue }) => {
    try {
      const response = await rejectOfferRevision(offerId, revisionId, payload);
      if (response?.missing || response?.status === 404) {
        return rejectWithValue({ ...response, missing: true });
      }
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return { offerId, revisionId, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitOfferRevisionForReviewAsync = createAsyncThunk(
  "offers/submitOfferRevisionForReviewAsync",
  async ({ offerId, revisionId, ...payload }, { rejectWithValue }) => {
    try {
      const response = await submitOfferRevisionForReview(
        offerId,
        revisionId,
        payload
      );
      if (response?.missing || response?.status === 404) {
        return rejectWithValue({ ...response, missing: true });
      }
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return { offerId, revisionId, ...response };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const convertOfferToProjectAsync = createAsyncThunk(
  "offers/convertOfferToProjectAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await convertOfferToProject(payload);
      if (
        isOfferThunkError(response) ||
        response?.success === false
      ) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const generateOfferLinesFromCalculationAsync = createAsyncThunk(
  "offers/generateOfferLinesFromCalculationAsync",
  async (offerId, { rejectWithValue }) => {
    try {
      const response = await generateOfferLinesFromCalculation(offerId);
      if (response?.missing || response?.status === 404) {
        return rejectWithValue({ ...response, missing: true });
      }
      if (isOfferThunkError(response)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const offerSlice = createSlice({
  name: "offers",
  initialState,
  reducers: {
    patchOfferTotals(state, action) {
      const totals = action.payload || {};
      if (!state.offer || typeof state.offer !== "object") return;
      if (totals.subtotal_amount != null) {
        state.offer.subtotal_amount = totals.subtotal_amount;
      }
      if (totals.cost_total != null) {
        state.offer.cost_total = totals.cost_total;
      }
      if (totals.contribution_margin != null) {
        state.offer.contribution_margin = totals.contribution_margin;
      }
      if (totals.contribution_margin_percent != null) {
        state.offer.contribution_margin_percent = totals.contribution_margin_percent;
      }
      // Always bump so OfferLinesTab refetches even when timestamps collide
      // in the same second. Prefer API updated_at when present.
      state.offer.updated_at = totals.updated_at || new Date().toISOString();
      state.offer.presentation_epoch = (state.offer.presentation_epoch || 0) + 1;
    },
    patchOfferCalculationSnapshot(state, action) {
      const saved = action.payload;
      if (!state.offer || typeof state.offer !== "object" || saved?.id == null) {
        return;
      }
      const list = Array.isArray(state.offer.calculations)
        ? state.offer.calculations
        : [];
      const idx = list.findIndex((c) => String(c?.id) === String(saved.id));
      const prev = idx >= 0 ? list[idx] : {};
      const nextCalc = { ...prev, ...saved };
      delete nextCalc.offer_totals;
      // Keep previous items only when the payload omitted `items`. An explicit
      // empty array is a successful save of a calc with no lines.
      if (!Object.prototype.hasOwnProperty.call(saved, "items")) {
        if (Array.isArray(prev.items) && prev.items.length > 0) {
          nextCalc.items = prev.items;
        } else {
          delete nextCalc.items;
        }
      }
      state.offer.calculations =
        idx >= 0
          ? list.map((c, i) => (i === idx ? nextCalc : c))
          : [...list, nextCalc];
      if (saved.total_amount != null && state.offer.subtotal_amount == null) {
        state.offer.subtotal_amount = saved.total_amount;
      }
      state.offer.presentation_epoch = (state.offer.presentation_epoch || 0) + 1;
      if (saved.updated_at) {
        state.offer.updated_at = saved.updated_at;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOffersAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getOffersAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newestOffersCount = action.payload.newestOffersCount;
        state.offerFeatureFlags = resolveOfferFeatureFlags(
          action.payload,
          state.offerFeatureFlags
        );
      })
      .addCase(getOffersAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente tilbudene.");
      })
      .addCase(updateOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.offer = action.payload;
          draftState.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload,
            draftState.offerFeatureFlags
          );
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateOfferAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere tilbudet.");
      })
      .addCase(createOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.offer = action.payload;
          draftState.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload,
            draftState.offerFeatureFlags
          );
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createOfferAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette tilbudet.");
      })
      .addCase(getOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const epoch = draftState.offer?.presentation_epoch;
          draftState.offer = action.payload;
          if (epoch) {
            draftState.offer.presentation_epoch = epoch;
          }
          draftState.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload,
            draftState.offerFeatureFlags
          );
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(getOfferAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente tilbudet.");
      })
      .addCase(deleteOfferAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteOfferAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(deleteOfferAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke slette tilbudet.");
      })
      .addCase(getOfferRevisionsAsync.pending, (state) => {
        state.revisionsLoading = true;
        state.revisionsError = null;
      })
      .addCase(getOfferRevisionsAsync.fulfilled, (state, action) => {
        state.revisionsLoading = false;
        state.revisionsError = null;
        state.revisionsOfferId = action.payload.offerId;
        state.revisions = action.payload.data || [];
        if (action.payload.current_draft_revision_id !== undefined) {
          state.currentDraftRevisionId =
            action.payload.current_draft_revision_id;
        }
        if (action.payload.accepted_revision_id !== undefined) {
          state.acceptedRevisionId = action.payload.accepted_revision_id;
        }
      })
      .addCase(getOfferRevisionsAsync.rejected, (state, action) => {
        state.revisionsLoading = false;
        state.revisions = [];
        state.revisionsError =
          action.payload?.status === 403 ||
          action.payload?.error === "not_authorized"
            ? "not_authorized"
            : action.payload?.errors?.[0] ||
              action.payload?.error ||
              "Failed to load revisions";
      })
      .addCase(getOfferActivityAsync.pending, (state) => {
        state.activityLoading = true;
        state.activityError = null;
      })
      .addCase(getOfferActivityAsync.fulfilled, (state, action) => {
        state.activityLoading = false;
        state.activityError = null;
        state.activityOfferId = action.payload.offerId;
        state.activityCategory = action.payload.category || "all";
        state.activity = action.payload.data || [];
      })
      .addCase(getOfferActivityAsync.rejected, (state, action) => {
        state.activityLoading = false;
        state.activity = [];
        state.activityError =
          action.payload?.status === 403 ||
          action.payload?.error === "not_authorized"
            ? "not_authorized"
            : action.payload?.errors?.[0] ||
              action.payload?.error ||
              "Failed to load activity";
      })
      .addCase(createOfferRevisionAsync.fulfilled, (state, action) => {
        const revision = action.payload.revision;
        if (revision?.id) {
          const idx = state.revisions.findIndex((r) => r.id === revision.id);
          if (idx >= 0) {
            state.revisions[idx] = { ...state.revisions[idx], ...revision };
          } else {
            state.revisions = [...state.revisions, revision].sort(
              (a, b) =>
                (a.revision_number || 0) - (b.revision_number || 0)
            );
          }
        }
        if (revision?.current_draft_revision_id !== undefined) {
          state.currentDraftRevisionId = revision.current_draft_revision_id;
        } else if (revision?.id && revision?.status === "draft") {
          state.currentDraftRevisionId = revision.id;
        }
      })
      .addCase(acceptOfferAsync.fulfilled, (state, action) => {
        if (action.payload?.offer) {
          state.offer = action.payload.offer;
          state.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload.offer,
            state.offerFeatureFlags
          );
        }
        if (action.payload?.accepted_revision_id !== undefined) {
          state.acceptedRevisionId = action.payload.accepted_revision_id;
        }
        const accepted = action.payload?.accepted_revision;
        if (accepted?.id) {
          const idx = state.revisions.findIndex((r) => r.id === accepted.id);
          if (idx >= 0) {
            state.revisions[idx] = {
              ...state.revisions[idx],
              ...accepted,
            };
          }
        }
      })
      .addCase(approveOfferRevisionAsync.fulfilled, (state, action) => {
        if (action.payload?.offer) {
          state.offer = action.payload.offer;
          state.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload.offer,
            state.offerFeatureFlags
          );
        }
        if (action.payload?.current_draft_revision_id !== undefined) {
          state.currentDraftRevisionId =
            action.payload.current_draft_revision_id;
        }
        const approved =
          action.payload?.approved_revision ||
          action.payload?.revision ||
          null;
        if (approved?.id) {
          const idx = state.revisions.findIndex((r) => r.id === approved.id);
          if (idx >= 0) {
            state.revisions[idx] = { ...state.revisions[idx], ...approved };
          } else {
            state.revisions = [...state.revisions, approved];
          }
        }
        const draft = action.payload?.draft_revision;
        if (draft?.id) {
          const idx = state.revisions.findIndex((r) => r.id === draft.id);
          if (idx >= 0) {
            state.revisions[idx] = { ...state.revisions[idx], ...draft };
          } else {
            state.revisions = [...state.revisions, draft];
          }
          state.currentDraftRevisionId = draft.id;
        }
      })
      .addCase(rejectOfferRevisionAsync.fulfilled, (state, action) => {
        if (action.payload?.offer) {
          state.offer = action.payload.offer;
          state.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload.offer,
            state.offerFeatureFlags
          );
        }
        const rejected =
          action.payload?.rejected_revision ||
          action.payload?.revision ||
          null;
        if (rejected?.id) {
          const idx = state.revisions.findIndex((r) => r.id === rejected.id);
          if (idx >= 0) {
            state.revisions[idx] = { ...state.revisions[idx], ...rejected };
          } else {
            state.revisions = [...state.revisions, rejected];
          }
        }
      })
      .addCase(submitOfferRevisionForReviewAsync.fulfilled, (state, action) => {
        if (action.payload?.offer) {
          state.offer = action.payload.offer;
          state.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload.offer,
            state.offerFeatureFlags
          );
        }
        if (action.payload?.current_draft_revision_id !== undefined) {
          state.currentDraftRevisionId =
            action.payload.current_draft_revision_id;
        }
        const submitted = action.payload?.revision || null;
        if (submitted?.id) {
          const idx = state.revisions.findIndex((r) => r.id === submitted.id);
          if (idx >= 0) {
            state.revisions[idx] = { ...state.revisions[idx], ...submitted };
          } else {
            state.revisions = [...state.revisions, submitted];
          }
        }
      })
      .addCase(convertOfferToProjectAsync.fulfilled, (state, action) => {
        if (action.payload?.offer) {
          state.offer = action.payload.offer;
          state.offerFeatureFlags = resolveOfferFeatureFlags(
            action.payload.offer,
            state.offerFeatureFlags
          );
        }
      })
      .addCase(generateOfferLinesFromCalculationAsync.fulfilled, (state, action) => {
        const offer = action.payload?.offer || action.payload;
        if (offer?.id) {
          state.offer = offer;
          state.offerFeatureFlags = resolveOfferFeatureFlags(
            offer,
            state.offerFeatureFlags
          );
        }
      });
  },
});

export const { patchOfferTotals, patchOfferCalculationSnapshot } = offerSlice.actions;
export default offerSlice.reducer;

